/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type {
  ConfigQueryResults,
  FIMOConfiguration,
  FIMOSimulationRequestWithName,
  OptionalCompoundId,
} from '@poweralpha/common';
import { useQuery } from '@tanstack/react-query';
import { api, getQueryConfigsSearchParams, type QueryConfigsSearchParams } from '../api';
import type { ConfigType } from '../react-components/configs/models';
import type { QueryOptions } from '../react-components/queries';

export const getFIMOConfigurations = async <T = FIMOConfiguration>(
  queryOptions?: QueryConfigsSearchParams,
): Promise<ConfigQueryResults<T>> => {
  const searchParams = getQueryConfigsSearchParams(queryOptions);
  return api.get('fimo/configs', { searchParams }).json();
};

export const getFIMOConfigMaxVersion = async (id: string | undefined | null): Promise<number> =>
  api.get(`fimo/configs/${id}/max-version`).json();

export const getFIMOConfiguration = async (
  id: string | undefined | null,
  version?: number,
): Promise<FIMOConfiguration> => {
  const response = await api
    .get(`fimo/configs/${id}`, version !== undefined ? { searchParams: { version } } : {})
    .json<FIMOConfiguration>();
  return { ...response, id: id ?? '' };
};

export const saveFIMOConfiguration = async (
  request: FIMOSimulationRequestWithName,
): Promise<FIMOConfiguration> => api.post('fimo/configs', { json: request }).json();

export const editFIMOConfiguration = async (
  request: FIMOSimulationRequestWithName,
  id: string | undefined | null,
  version: number,
): Promise<FIMOConfiguration> =>
  api.post(`fimo/configs/${id}/${version}`, { json: request }).json();

export const useFimoConfigQuery = (
  id: OptionalCompoundId | undefined,
  opts?: QueryOptions<FIMOConfiguration>,
) =>
  useQuery({
    queryKey: ['fimo-config' satisfies `${ConfigType}-config`, ...(id ?? [])],
    queryFn: () => getFIMOConfiguration(id?.[0], id?.[1]),
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: id?.[0] ? opts?.enabled : false,
  });
